// Provides form mode (create/edit/view/admin-view) to all form components
import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';

export type FormMode = 'create' | 'edit' | 'view' | 'admin-view';

export interface FormModeContextValue {
  mode: FormMode;
  isReadOnly: boolean;
  isEditable: boolean;
  showActions: boolean;
}

export const FormModeContext = createContext<FormModeContextValue | null>(null);

interface Props {
  mode: FormMode;
  children: ReactNode;
}

export function FormModeProvider({ mode, children }: Props) {
  const value = useMemo<FormModeContextValue>(() => {
    const isReadOnly = mode === 'view' || mode === 'admin-view';
    return {
      mode,
      isReadOnly,
      isEditable: !isReadOnly,
      showActions: !isReadOnly,
    };
  }, [mode]);

  return <FormModeContext.Provider value={value}>{children}</FormModeContext.Provider>;
}

// Hook to access form mode context
export function useFormMode(): FormModeContextValue {
  const ctx = useContext(FormModeContext);
  if (!ctx) {
    throw new Error('useFormMode must be used within FormModeProvider');
  }
  return ctx;
}
